import { useCallback, useMemo, useState } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  checkTemplateSaveDisabled,
  createReservationTemplate,
  deleteReservationTemplateItem,
  firstSelectedReservationTemplateSelector,
  secondSelectedReservationTemplateSelector,
  selectedReservationTemplateMap,
  setSelectedReservationTemplate,
  updateReservationTemplate,
} from 'slices/reservationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { KeyValueCallback } from 'types/utils.type';
import type { TReservationTemplate } from '@timeedit/types/lib/types';
import { TEDrawer } from '@timeedit/ui-components';
import ReservationTemplate from './ReservationTemplate';
import './ReservationTemplateDrawer.scss';

interface Props {
  confirmDiscardTemplateChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const ReservationTemplateDrawer = ({
  confirmDiscardTemplateChanges,
}: Props) => {
  const dispatch = useAppDispatch();
  const templateById: Record<number, TReservationTemplate> = useAppSelector(
    selectedReservationTemplateMap
  );
  const firstTemplate: TReservationTemplate = useAppSelector(
    firstSelectedReservationTemplateSelector
  );
  const secondTemplate: TReservationTemplate = useAppSelector(
    secondSelectedReservationTemplateSelector
  );

  const firstTemplateSaveDisabled = useMemo(
    () => dispatch(checkTemplateSaveDisabled(true)),
    [firstTemplate]
  );
  const secondTemplateSaveDisabled = useMemo(
    () => dispatch(checkTemplateSaveDisabled(false)),
    [secondTemplate]
  );

  const onClose = (isFirstTemplate: boolean) => {
    confirmDiscardTemplateChanges(isFirstTemplate, () => {
      dispatch(setSelectedReservationTemplate(isFirstTemplate));
    });
  };

  const onDelete = (template: TReservationTemplate) => {
    dispatch(deleteReservationTemplateItem(template.id));
  };

  const onSave = (template: TReservationTemplate) => {
    if (template.id === 0) {
      dispatch(createReservationTemplate(template));
    } else {
      dispatch(updateReservationTemplate(template));
    }
  };
  const onDiscard = (isFirstTemplate: boolean, templateId: number) => {
    const template = templateById[templateId];
    dispatch(setSelectedReservationTemplate(isFirstTemplate, template));
  };

  const setFirstResource = useCallback(
    (template: TReservationTemplate) =>
      dispatch(setSelectedReservationTemplate(true, template)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (template: TReservationTemplate) =>
      dispatch(setSelectedReservationTemplate(false, template)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    templateById,
    firstTemplate,
    secondTemplate,
    setFirstResource,
    setSecondResource
  );

  const [isExpand, setIsExpand] = useState(false);

  const onFirstTemplateChanged: KeyValueCallback<TReservationTemplate> = (
    property,
    value
  ) => {
    dispatch(
      setSelectedReservationTemplate(true, {
        ...firstTemplate,
        [property]: value,
      })
    );
  };

  const onSecondTemplateChanged: KeyValueCallback<TReservationTemplate> = (
    property,
    value
  ) => {
    dispatch(
      setSelectedReservationTemplate(false, {
        ...secondTemplate,
        [property]: value,
      })
    );
  };
  return (
    <>
      <TEDrawer
        expandWidth="70vw"
        firstDrawer={{
          title: firstTemplate?.name,
          content: (
            <ReservationTemplate
              template={firstTemplate}
              onChange={onFirstTemplateChanged}
              isExpand={isExpand}
            />
          ),
          drawerOpen: !!firstTemplate,
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText(
                'reservation_template_confirmation_delete'
              )}
              handleDelete={() => onDelete(firstTemplate)}
              handleDiscard={() => onDiscard(true, firstTemplate.id)}
              handleSave={() => onSave(firstTemplate)}
              isSaveDisable={firstTemplateSaveDisabled as any}
            />
          ),
          setDrawerOpen: (open) => !open && onClose(true),
        }}
        secondDrawer={{
          title: secondTemplate?.name,
          content: (
            <ReservationTemplate
              template={secondTemplate}
              onChange={onSecondTemplateChanged}
              isExpand={isExpand}
            />
          ),
          drawerOpen: !!secondTemplate,
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText(
                'reservation_template_confirmation_delete'
              )}
              handleDelete={() => onDelete(secondTemplate)}
              handleDiscard={() => onDiscard(true, secondTemplate.id)}
              handleSave={() => onSave(secondTemplate)}
              isSaveDisable={secondTemplateSaveDisabled as any}
            />
          ),
          setDrawerOpen: (open) => !open && onClose(false),
        }}
        onExpand={setIsExpand}
      />
    </>
  );
};

export default ReservationTemplateDrawer;
